import React from "react"
import SubSectorPieChart from "../QuarterlyReport/SubSectorPieChart"
import { SubSectorExposureData } from "~/api/quarterly_reports"

const EmptyPieChart = () => {
  return (
    <div className="editorjs-empty-tool-table">
      No sub-sector data available
    </div>
  )
}

const hasValidPieChartData = (data: any) => {
  return data && Array.isArray(data) && data.length > 0
}

export class SubSectorPieChartTool {
  api: any
  block: any
  createPortal: any
  removePortal: any
  portal: any
  subSectorData: SubSectorExposureData[]
  headerInserted: boolean

  constructor(toolConfig: any) {
    this.block = toolConfig.block
    this.api = toolConfig.api
    this.createPortal = toolConfig.config?.createPortal
    this.removePortal = toolConfig.config?.removePortal
    this.subSectorData = toolConfig.config?.subSectorData

    this.headerInserted = toolConfig.data?.header_inserted || false

    return this
  }

  static get toolbox() {
    return {
      title: "Sub-Sector Pie Chart",
      icon: '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><path d="M12 2v10l8.66 5"/></svg>',
    }
  }

  render() {
    const rootEl = document.createElement("div")

    const hasData = hasValidPieChartData(this.subSectorData)

    // Check if we need to insert a header above this block
    queueMicrotask(() => {
      this.checkAndInsertHeaderIfNeeded()
    })

    this.portal = this.createPortal(
      this.block.id,
      hasData ? (
        <SubSectorPieChart data={this.subSectorData} />
      ) : (
        <EmptyPieChart />
      ),
      rootEl,
    )

    return rootEl
  }

  async checkAndInsertHeaderIfNeeded() {
    try {
      // Short-circuit if header has already been inserted
      if (this.headerInserted) {
        return
      }

      // Find this block's index by searching through all blocks
      let currentBlockIndex = -1
      const totalBlocks = this.api.blocks.getBlocksCount()

      for (let i = 0; i < totalBlocks; i++) {
        const block = this.api.blocks.getBlockByIndex(i)
        if (block && block.id === this.block.id) {
          currentBlockIndex = i
          break
        }
      }

      if (currentBlockIndex === -1) {
        console.error(
          "ERROR: Could not find current block, skipping header insertion",
        )
        return
      }

      if (currentBlockIndex > 0) {
        const previousBlock = this.api.blocks.getBlockByIndex(
          currentBlockIndex - 1,
        )

        if (previousBlock) {
          // Await the saved data from the previous block
          const previousBlockData = await previousBlock.save()

          // Check if it's a header block with our specific text and level
          if (
            previousBlockData.tool === "header" &&
            previousBlockData.data.text === "Exposure by Sub-Sector" &&
            previousBlockData.data.level === 3
          ) {
            // Header exists - mark as inserted even though we didn't insert it
            this.headerInserted = true
            return
          }
        }
      }

      this.insertHeaderBlock(currentBlockIndex)
    } catch (error) {
      console.error("Error in checkAndInsertHeaderIfNeeded:", error)
    }
  }

  insertHeaderBlock(currentBlockIndex: number) {
    try {
      // Insert a header block above the current pie chart block
      this.api.blocks.insert(
        "header",
        {
          text: "Exposure by Sub-Sector",
          level: 3,
        },
        {},
        currentBlockIndex,
        true,
      )

      this.headerInserted = true

      // Move focus back to the pie chart block after insertion
      setTimeout(() => {
        this.api.caret.setToBlock(currentBlockIndex + 1)
      }, 50)
    } catch (error) {
      console.error("Error inserting header block:", error)
    }
  }

  remove() {
    const totalBlocks = this.api.blocks.getBlocksCount()
    for (let i = 0; i < totalBlocks; i++) {
      const block = this.api.blocks.getBlockByIndex(i)
      if (block && block.id === this.block.id) {
        this.api.blocks.delete(i)
        break
      }
    }
  }

  removed() {
    this.removePortal(this.block.id)
  }

  save() {
    return {
      type: "sub_sector_pie_chart",
      header_inserted: this.headerInserted,
    }
  }

  static get isReadOnlySupported() {
    return true
  }
}
